import { PROTOCOL_TEST_IDS } from '@/components/admin/protocoles/contracts';
import { FarewellBadge } from '@/components/admin/protocoles/timeline/farewell-badge';
import { Tooltip } from '@/components/ui/tooltip';
import { countLabel, problemCountLabel } from '@/lib/protocol-labels';
import { cn } from '@/lib/utils';
import type { ProtocolStepDraft } from '@matter_extractor/shared';
import { experienceExitsOf, isEmptyStep } from '@matter_extractor/shared';
import { CircleX, GripVertical, Repeat, Trash2, TriangleAlert } from 'lucide-react';
import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';

/**
 * One card of the frise (refonte modulaire §7.1; maquette `.step-card`).
 *
 * « une carte par étape (numéro, nom, nombre d'objectifs, plafond ; badges du
 * nombre d'essais quand il dépasse un et des sorties "fermer l'expérience" ;
 * triangle jaune quand l'enregistrement d'une version refusera l'étape) », and
 * since issue #163 the badge « adieu » on a farewell step, whose card says « un
 * message · sans réponse » in place of the objectives and the cap. The
 * card is a SUMMARY and says each fact in the fewest words that stay true - the
 * step editor beside it is where every one of them is written out.
 *
 * ## The badges are `aria-hidden`, and that is not an omission
 *
 * Each carries a drawn bubble rather than a native `title` (issue #103 -
 * `components/ui/tooltip.tsx` says why the drawn one is white, and why the
 * native one was the defect that opened the lot). Announced as well, they
 * would join the card's own accessible name (a `role="button"` is named by its
 * content), and « 3 · Émergence 4 obj. · 10 tours 3 problèmes jusqu'à 2 essais »
 * is what every arrow key would then read out. The facts stay reachable where
 * they are actionable: the head's « N problèmes » pill lists them with a link
 * per step (§7.1), and the step's own panels state the attempts and the exits.
 *
 * ## The trash lives here and only on an EMPTY step
 *
 * §7.1: « une étape se supprime depuis son contenu, avec une confirmation qui
 * compte les sauts qui la visent ; une étape VIDE porte sa corbeille sur la
 * carte ». An empty step holds nothing and is aimed at by nothing worth
 * counting, so it needs no box - and its click must not also select the card,
 * which is why it stops the event.
 */

export interface StepCardProps {
  readonly step: ProtocolStepDraft;
  readonly index: number;
  readonly selected: boolean;
  /** « Voir » or a retired protocol: no trash, no drag (§7.1). */
  readonly readOnly: boolean;
  /** How many problems `protocolProblems` names on this step - the yellow triangle. */
  readonly problems: number;
  readonly dragging: boolean;
  readonly onSelect: (stepId: string) => void;
  readonly onRemoveEmpty: (stepId: string) => void;
  readonly onDragStart: (stepId: string) => void;
  readonly onDragEnd: () => void;
  /** The frise MEASURES this node to place the lanes - see `lib/protocol-lanes.ts`. */
  readonly registerNode: (stepId: string, node: HTMLElement | null) => void;
}

/**
 * What the card writes beside its number, and what the « + » beside it names in
 * « Insérer une étape avant ... ».
 *
 * The same fallback as `stepTitle` (« Étape 4 ») without the rank, which the
 * card already carries in its own tab - and exported so the insertion button
 * and the card can never call one step by two names.
 */
export function stepCardName(step: ProtocolStepDraft, index: number): string {
  const name = step.name.trim();
  return name === '' ? `Étape ${index + 1}` : name;
}

/** The pill of a badge: one ground, one ink, one icon, one number at most. */
function Badge({
  className,
  title,
  children,
}: {
  className: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <Tooltip label={title}>
      <span
        aria-hidden="true"
        className={cn(
          'inline-flex h-4 items-center gap-0.5 rounded-pill px-1.5 font-semibold text-[10.5px] tabular-nums',
          className,
        )}
      >
        {children}
      </span>
    </Tooltip>
  );
}

export function StepCard({
  step,
  index,
  selected,
  readOnly,
  problems,
  dragging,
  onSelect,
  onRemoveEmpty,
  onDragStart,
  onDragEnd,
  registerNode,
}: StepCardProps) {
  const empty = isEmptyStep(step);
  /*
    A farewell step says what it IS before what it holds (issue #163): « un
    message · sans réponse » rather than objectives and a cap it does not have,
    and never « étape vide » - its direction may well be empty while it is being
    written, and it is still the goodbye. Its trash stays: an empty step is
    deleted without a box whatever its nature.
  */
  const farewell = step.farewell;
  const exits = experienceExitsOf(step);
  // The attempts are a rule, and a farewell step's rules are ignored (H76).
  const attempts = farewell ? 1 : step.rules.attempts;
  const named = step.name.trim() !== '';
  const mandatory = step.objectives.some((objective) => objective.mandatory);

  function select(): void {
    onSelect(step.id);
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    // A space on a `role="button"` scrolls the page unless it is claimed here.
    event.preventDefault();
    select();
  }

  function removeEmpty(event: MouseEvent<HTMLButtonElement>): void {
    event.stopPropagation();
    onRemoveEmpty(step.id);
  }

  return (
    <div
      ref={(node) => registerNode(step.id, node)}
      // biome-ignore lint/a11y/useSemanticElements: a <button> may not contain the trash <button>.
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      data-testid={PROTOCOL_TEST_IDS.stepCard}
      data-step-id={step.id}
      draggable={!readOnly}
      onClick={select}
      onKeyDown={onKeyDown}
      onDragStart={() => onDragStart(step.id)}
      onDragEnd={onDragEnd}
      className={cn(
        'group relative h-16 w-50 flex-none cursor-pointer rounded-md border-2 bg-white px-2.5 py-2 text-left transition-colors',
        empty && !farewell ? 'border-dashed' : '',
        selected ? 'border-bleu-nuit bg-bleu-nuit-tint' : 'border-gray-200 hover:border-gray-400',
        dragging ? 'opacity-40' : '',
      )}
    >
      {/* The grip says « draggable » and nothing else: the drag is on the card. */}
      {readOnly ? null : (
        <GripVertical
          aria-hidden="true"
          className="-left-0.5 -translate-y-1/2 absolute top-1/2 size-3.5 cursor-grab text-gray-300 opacity-0 transition-opacity group-hover:opacity-100"
        />
      )}
      {/* The two rows keep clear of whatever is pinned to their right: a name
          that ran under a badge would be truncated by a shape, not by a width. */}
      <div
        className={cn(
          'flex items-center gap-1.5 overflow-hidden whitespace-nowrap',
          problems > 0 || attempts > 1 || farewell ? 'pr-12' : '',
        )}
      >
        <span className="flex-none rounded-sm bg-gray-100 px-1.5 font-semibold text-gray-700 text-xs tabular-nums">
          {index + 1}
        </span>
        {/* Grey when it is a PLACEHOLDER and not the step's name - what the card
            shows then is « Étape 4 », which the operator never typed. */}
        <span
          className={cn(
            'min-w-0 truncate text-sm',
            named ? 'font-semibold text-bleu-nuit' : 'text-gray-600',
          )}
        >
          {stepCardName(step, index)}
        </span>
      </div>
      <div
        className={cn(
          'mt-0.5 flex items-center gap-1.5 overflow-hidden whitespace-nowrap text-gray-600 text-xs tabular-nums',
          exits > 0 || (empty && !readOnly) ? 'pr-9' : '',
        )}
      >
        {farewell ? (
          'un message · sans réponse'
        ) : empty ? (
          'étape vide'
        ) : (
          <>
            {mandatory ? (
              <Tooltip label="au moins un objectif obligatoire">
                <span aria-hidden="true" className="size-1.5 flex-none rounded-full bg-bleu-nuit" />
              </Tooltip>
            ) : null}
            <span>
              {`${step.objectives.length} obj. · `}
              {step.rules.maxTurns === null ? (
                <span className="text-jaune-deep">sans plafond</span>
              ) : (
                countLabel(step.rules.maxTurns, 'tour')
              )}
            </span>
          </>
        )}
      </div>
      <span className="absolute top-1 right-1 flex flex-col items-end gap-1">
        {problems > 0 ? (
          <Badge className="bg-jaune-tint text-jaune-deep" title={problemCountLabel(problems)}>
            <TriangleAlert className="size-2.5" />
          </Badge>
        ) : null}
        {attempts > 1 ? (
          <Badge
            className="bg-bleu-ciel-tint text-bleu-ciel-deep"
            title={`jusqu’à ${attempts} essais`}
          >
            <Repeat className="size-2.5" />
            {attempts}
          </Badge>
        ) : null}
        {farewell ? <FarewellBadge /> : null}
      </span>
      {exits > 0 ? (
        <span className="absolute right-1 bottom-1">
          <Badge
            className="bg-rouge-tint text-rouge-deep"
            title={`${countLabel(exits, 'sortie')} : fermer l’expérience`}
          >
            <CircleX className="size-2.5" />
            {exits}
          </Badge>
        </span>
      ) : null}
      {empty && !readOnly ? (
        <button
          type="button"
          data-testid={PROTOCOL_TEST_IDS.removeEmptyStep}
          aria-label="Supprimer l’étape vide"
          onClick={removeEmpty}
          className="absolute right-1 bottom-1 inline-grid size-6 cursor-pointer place-items-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-rouge-deep"
        >
          <Trash2 aria-hidden="true" className="size-3.5" />
        </button>
      ) : null}
    </div>
  );
}
